import { ReactElement, useMemo } from 'react';
import {
  Dropdown,
  DropdownContainer,
  FlexCell,
  FlexRow,
  LinkButton,
  Text,
  Button,
} from '@epam/promo';
import { DropdownContainerProps } from '@epam/uui-components';
import { IDropdownToggler } from '@epam/uui-core';
import { ReactComponent as iconFilter } from '@epam/assets/icons/common/content-filtration-18.svg';
import { ReactComponent as iconNavigationClose } from '@epam/assets/icons/common/navigation-close-18.svg';
import { ReactComponent as iconClearFilters } from '@epam/assets/icons/common/content-clear-18.svg';
import { FiltersType } from 'hooks';
import css from './CardFilters.module.scss';

interface FilterProps {
  filters: FiltersType;
  isFiltersChanged: boolean;
  /** Draft toggles outside `filters` (e.g. favourites) — Clear All + filter icon */
  additionalDraftActive?: boolean;
  dropdownWidth?: number;
  onClear: () => void;
  onApply: () => void;
  children: ReactElement;
}
export const Filter = ({
  dropdownWidth = 282,
  filters,
  isFiltersChanged,
  additionalDraftActive = false,
  onClear,
  onApply,
  children,
}: FilterProps) => {
  const isSomeDraftActive = useMemo(
    () =>
      Object.values(filters).some((status) => status) || additionalDraftActive,
    [filters, additionalDraftActive],
  );

  const renderDropdownBody = (props: DropdownContainerProps) => (
    <DropdownContainer {...props}>
      <FlexRow
        borderBottom
        alignItems="center"
        background="gray5"
        columnGap="12"
        padding="12"
        vPadding="18">
        <FlexCell grow={1}>
          <Text color="gray90" fontSize="14">
            FILTER
          </Text>
        </FlexCell>
        {isSomeDraftActive && (
          <LinkButton
            caption="Clear All"
            color="blue"
            icon={iconClearFilters}
            onClick={() => {
              onClear();
              props.onClose?.();
            }}
          />
        )}
        <LinkButton
          color="gray60"
          icon={iconNavigationClose}
          onClick={() => {
            props.onClose?.();
          }}
        />
      </FlexRow>
      {children}
      <FlexRow alignItems="center" columnGap="12" padding="12" vPadding="24">
        <FlexCell width="100%">
          <Button
            caption="Apply"
            color="blue"
            cx={css.filterInput__apply}
            fill="solid"
            icon={iconFilter}
            isDisabled={!isFiltersChanged}
            onClick={() => {
              onApply();
              props.onClose?.();
            }}
          />
        </FlexCell>
      </FlexRow>
    </DropdownContainer>
  );

  return (
    <Dropdown
      placement="bottom-end"
      renderBody={(props) =>
        renderDropdownBody({
          ...props,
          focusLock: false,
          width: dropdownWidth,
          onClose: () => {
            props.onClose?.();
          },
        })
      }
      renderTarget={(props: IDropdownToggler) => (
        <LinkButton
          color={isSomeDraftActive ? 'blue' : 'gray60'}
          cx={css['taskInput__filter-icon']}
          icon={iconFilter}
          {...props}
        />
      )}
    />
  );
};
